// src/components/Contact.jsx
import React, { useState, useEffect } from "react";
import firebase from "../Firebase";
import { Formik } from "formik";
import * as Yup from "yup";
import { useLocation, useHistory } from "react-router-dom";
import Header from "./Header";
import './CompanyPages.css';

// 共通で使う電話番号バリデーション（正規表現を修正）
const phoneRegExp =
  /^((\+[1-9]{1,4}[\s-]*)|(\([0-9]{2,3}\)[\s-]*)|([0-9]{2,4})[\s-]*)*?[0-9]{3,4}?[\s-]*[0-9]{3,4}?$/;

// 送信処理（埋め込み/単独ページの両方から呼べるよう分離）
async function submitContact(values) {
  const db = firebase.firestore();
  // エミュレータ使用しない前提。必要ならここで settings を切替
  const docRef = await db.collection("contacts").add({
    companyName: values.companyName,
    email: values.email,
    tel: values.tel,
    content: values.content,
    companyUserName: values.companyUserName,
    createdAt: new Date(),
  });
  return docRef.id;
}

/**
 * 埋め込み用フォーム（LP から利用）
 * onSubmitted: (docId: string) => void を渡すとリダイレクトせず完了メッセージ表示などが可能
 */
export const ContactForm = ({ onSubmitted, showTitle = true }) => {
  const [doneId, setDoneId] = useState(null);

  return (
    <div className="form-top wc-form">
      {showTitle && <h2 className="pg2-title-contact">お問い合わせ</h2>}

      {/* 送信完了メッセージ */}
      {doneId && (
        <p className="mt-4 text-green-600 text-sm">
          お問い合わせを受け付けました。担当者よりご連絡します。
        </p>
      )}

      <Formik
        initialValues={{
          email: "",
          tel: "",
          companyName: "",
          content: "",
          companyUserName: "",
        }}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            const id = await submitContact(values);
            setDoneId(id);
            if (onSubmitted) onSubmitted(id);
            resetForm();
          } catch (e) {
            console.error("Error adding document: ", e);
            alert("送信に失敗しました。時間をおいて再度お試しください。");
          } finally {
            setSubmitting(false);
          }
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email("メールアドレスの形式が正しくありません")
            .required("メールアドレスは必須です"),
          tel: Yup.string()
            .matches(phoneRegExp, "電話番号の形式が正しくありません")
            .required("電話番号は必須です"),
          companyName: Yup.string().required("会社名は必須です"),
        })}
      >
        {({ handleSubmit, handleChange, handleBlur, values, errors }) => (
          <form onSubmit={handleSubmit} className="Form" noValidate>
            <div className="Form-Item-Top">
              <label htmlFor="companyName" className="Form-Item-Label">
                <span className="Form-Item-Label-Required">必須</span>会社名
              </label>
              <input
                style={{ fontSize: 16 }}
                type="text"
                className="Form-Item-Input"
                placeholder="Wondercoms, Inc."
                name="companyName"
                id="companyName"
                value={values.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-invalid={!!errors.companyName}
              />
            </div>
            <div className="error">{errors.companyName}</div>

            <div className="Form-Item">
              <label htmlFor="companyUserName" className="Form-Item-Label">
                名前
              </label>
              <input
                style={{ fontSize: 16 }}
                type="text"
                className="Form-Item-Input"
                name="companyUserName"
                id="companyUserName"
                value={values.companyUserName}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className="error">{errors.companyUserName}</div>

            <div className="Form-Item">
              <label htmlFor="tel" className="Form-Item-Label">
                <span className="Form-Item-Label-Required">必須</span>電話番号
              </label>
              <input
                style={{ fontSize: 16 }}
                type="tel"
                className="Form-Item-Input"
                placeholder="000-0000-0000"
                name="tel"
                id="tel"
                value={values.tel}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-invalid={!!errors.tel}
              />
            </div>
            <div className="error">{errors.tel}</div>

            <div className="Form-Item">
              <label htmlFor="email" className="Form-Item-Label">
                <span className="Form-Item-Label-Required">必須</span>
                メールアドレス
              </label>
              <input
                style={{ fontSize: 16 }}
                name="email"
                id="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                type="email"
                className="Form-Item-Input"
                placeholder="例）example@gmail.com"
                aria-invalid={!!errors.email}
              />
            </div>
            <div className="error">{errors.email}</div>

            <div className="Form-Item">
              <label className="Form-Item-Label isMsg" htmlFor="content">
                お問い合わせ内容
              </label>
              <textarea
                style={{ fontSize: 16 }}
                className="Form-Item-Textarea"
                name="content"
                id="content"
                value={values.content}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>

            <input type="submit" className="Form-Btn" value="送信" />
          </form>
        )}
      </Formik>
    </div>
  );
};

// 既存の /contact ページ用（Header を表示し、送信後は遷移）
const Contact = (props) => {
  const { pathname } = useLocation();
  const history = useHistory();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="home">
      <Header />
      <main className="wc-info wc-contact-page">
        <div className="wc-info-container">
          <div className="wc-info-intro">
            <p className="wc-info-eyebrow">CONTACT</p>
            <h1>お問い合わせ</h1>
            <p className="wc-info-lead">開発したいものや、今の業務で困っていることをお聞かせください。<br />詳しい仕様が決まっていなくても、ご相談いただけます。</p>
          </div>
          <ContactForm
            onSubmitted={() => history.push({ pathname: "/contact-complete" })}
            showTitle={false}
          />
        </div>
      </main>
    </div>
  );
};

export default Contact;
